import { useState } from 'react';
import Image from 'next/image';
import { MailIcon, Typography } from '@eventbrite/marmalade';
import { CrownIcon } from './_shared/CrownIcon';
import { TopOrganizerLabel } from './_shared/TopOrganizerLabel';
import { ContactButton } from './ContactButton/ContactButton';
import { ContactModal } from './ContactButton/ContactModal';
import { ExpandableBio } from './ExpandableBio/ExpandableBio';
import { Metrics } from './Metrics/Metrics';
import { SocialLinks, socialLinksStyles } from './SocialLinks/SocialLinks';
import { useOrganizerQualitySignals } from '../useOrganizerQualitySignals';
import type { OrganizerData } from '@/types/organizer';
import { FollowButton } from '../_shared/FollowButton';
import { OrganizerBannerBackground } from '../_shared/OrganizerBannerBackground';
import { LocationBadges } from './LocationBadges';
import { ShareProfile } from './ShareProfile/ShareProfile';
import { OwnerBanner } from './OwnerBanner/OwnerBanner';
import { SuperOrganizerAnnouncementModal } from './SuperOrganizerAnnouncementModal/SuperOrganizerAnnouncementModal';
import { useMarkAnnouncementSeen } from './useMarkAnnouncementSeen';
import styles from './OrganizerInfo.module.scss';

interface Props {
  data: OrganizerData;
  isOwner: boolean;
  refreshPhase: 'pending' | 'refreshing' | 'done';
  eventbriteBaseUrl: string;
}

export default function OrganizerInfo({ data, isOwner, refreshPhase, eventbriteBaseUrl }: Props) {
  const qualitySignalsQuery = useOrganizerQualitySignals(data.id);
  const qs = qualitySignalsQuery.data;
  const isSuperOrganizer = qs?.isSuperOrganizer === true;
  const verifiedDisplay = Boolean(data.verified || qs?.verified);
  const hasAvatar = data.avatarUrl.trim().length > 0;
  const initial = data.name.charAt(0).toUpperCase();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);
  const markAnnouncementSeen = useMarkAnnouncementSeen();
  const showAnnouncement =
    !announcementDismissed &&
    isOwner &&
    isSuperOrganizer &&
    qs?.superOrganizerAnnouncementSeen === false;

  const handleAnnouncementClose = () => {
    setAnnouncementDismissed(true);
    markAnnouncementSeen.mutate(data.id);
  };

  const editProfileUrl = `${eventbriteBaseUrl}/organizations/info/profile/${data.id}`;

  return (
    <>
      {isOwner && refreshPhase !== 'pending' && (
        <OwnerBanner editProfileUrl={editProfileUrl} refreshPhase={refreshPhase} />
      )}
      <OrganizerBannerBackground
        className={styles.cover}
        coverUrl={data.coverUrl}
        avatarUrl={data.avatarUrl}
        imageTestId="organizer-cover-background"
      />

      <div className={styles.headerWrap}>
        <div className={styles.header}>
          <div className={styles.avatarColumn}>
            <div className={styles.avatar}>
              {hasAvatar ? (
                <Image
                  src={data.avatarUrl}
                  alt={data.name}
                  fill
                  sizes="(max-width: 639px) 5.5rem, 12.5rem"
                />
              ) : (
                <span className={styles.avatarInitial}>{initial}</span>
              )}
              {isSuperOrganizer && (
                <span className={styles.avatarBadge}>
                  <CrownIcon />
                </span>
              )}
            </div>
            {isSuperOrganizer && <TopOrganizerLabel />}
          </div>

          <div className={styles.info}>
            <div className={styles.infoTop}>
              <div className={styles.infoContent}>
                <div
                  className={`${styles.nameRow} ${isSuperOrganizer ? styles.superOrganizerNameRow : ''}`}
                >
                  {isSuperOrganizer && (
                    <span className={styles.mobileTopOrganizerLabel}>Top Organizer</span>
                  )}
                  <Typography variant="heading-md" className={styles.nameText}>
                    {data.name}
                  </Typography>
                </div>
                <div className={styles.locationsBlock}>
                  <LocationBadges locations={data.locations} />
                </div>
                <div className={styles.metricsBlock}>
                  <Metrics
                    organizerId={data.id}
                    hideAttendeeCount={data.hideAttendeeCount}
                    hideTotalEvents={data.hideTotalEvents}
                  />
                </div>
                <div className={styles.bioBlock}>
                  <ExpandableBio html={data.bio ?? ''} />
                </div>
                <div className={styles.socialBlock}>
                  <SocialLinks socials={data.socials}>
                    <button
                      type="button"
                      className={`${socialLinksStyles.socialBtn} ${styles.mobileContactButton}`}
                      onClick={() => setIsContactModalOpen(true)}
                      aria-label="Contact organizer"
                    >
                      <MailIcon aria-hidden />
                    </button>
                  </SocialLinks>
                </div>
              </div>

              <div className={styles.actions}>
                <FollowButton organizerId={data.id} eventbriteBaseUrl={eventbriteBaseUrl} />
                <div className={styles.contactAction}>
                  <ContactButton organizerId={data.id} eventbriteBaseUrl={eventbriteBaseUrl} />
                </div>
                <ShareProfile organizer={data} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <ContactModal
        isShown={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        organizerId={data.id}
      />
      <SuperOrganizerAnnouncementModal
        isShown={showAnnouncement}
        onClose={handleAnnouncementClose}
        organizer={data}
        isVerified={verifiedDisplay}
      />
    </>
  );
}
